import { useCallback, useState } from "react";

import useInterval from "seneca-common/utils/hooks/useInterval";

type CountdownOptions<TFormatter> = {
  countStart: number;
  intervalMs?: number;
  isIncrement?: boolean;
  countStop?: number;
  autoStart?: boolean;
  formatter?: (count: number) => TFormatter;
};

interface CountdownReturn<TFormatter> {
  /** The current count value, formatted if `formatter` is passed */
  count: TFormatter;
  /** Starts the countdown */
  startCountdown: () => void;
  /** Stops the countdown without resetting the count */
  stopCountdown: () => void;
  /**
   * Resets the countdown to initial state (`countStart` value) without starting it.
   */
  resetCountdown: () => void;
  /**
   * Restarts the countdown from the initial state (`countStart` value), starting a new countdown immediately.
   */
  restartCountdown: () => void;
  /** Whether the countdown is currently running */
  isActive: boolean;
}

export function useCountdown<TFormatter = number>({
  countStart,
  countStop = 1,
  intervalMs = 1000,
  isIncrement = false,
  autoStart = true,
  formatter
}: CountdownOptions<TFormatter>): CountdownReturn<TFormatter> {
  const [count, setCount] = useState(countStart);
  const resetCounter = useCallback(() => setCount(countStart), [countStart]);

  const [isCountdownRunning, setIsCountdownRunning] = useState(autoStart);

  const restartCountdown = useCallback(() => {
    setIsCountdownRunning(false);
    resetCounter();
    setIsCountdownRunning(true);
  }, [resetCounter]);

  const resetCountdown = useCallback(() => {
    setIsCountdownRunning(false);
    resetCounter();
  }, [resetCounter]);

  useInterval(
    () => {
      if (count === countStop) {
        setIsCountdownRunning(false);
        return;
      }

      if (isIncrement) {
        setCount(count => (count += 1));
      } else {
        setCount(count => (count -= 1));
      }
    },
    isCountdownRunning ? intervalMs : null
  );

  return {
    count: (formatter ? formatter(count) : count) as TFormatter,
    startCountdown: () => setIsCountdownRunning(true),
    stopCountdown: () => setIsCountdownRunning(false),
    resetCountdown,
    restartCountdown,
    isActive: isCountdownRunning
  };
}
